import { Controller } from "@hotwired/stimulus"

// "Hidden from client" toggle (card #3204) -- an account manager's explicit
// call to hide ONE slide from every client-facing surface (Present/share/
// TV/Insights/PDF/PPTX) without touching its review status (approved/
// deciding/rejected are a completely separate concern -- see
// Deck#client_facing_slides).
//
// UNLIKE the pins toggle and Prior cycle toggle beside it in the same
// toolbar cluster (deck_slide_pins_toggle_controller.js /
// deck_slide_prior_toggle_controller.js -- both purely client-side, no
// server round-trip), this one PERSISTS: Deck#client_facing_slides must see
// the change on every FUTURE render, not just this browser tab. A click
// PATCHes DecksController#toggle_slide_hidden, mirroring
// deck_show_editor_controller.js's own fetch/CSRF/optimistic-update shape.
//
// OPTIMISTIC: the button's icon/state and the slide's own `.is-hidden`
// grey-out treatment (application.css) both flip immediately on click,
// before the response lands -- a failed PATCH reverts both rather than
// leaving a silently-unsaved change on screen (same trade-off
// deck_show_editor_controller.js's canvas edits make).
//
// OTHER open tabs on this deck pick up the toolbar's own icon state via
// DecksController#toggle_slide_hidden's broadcast_slide_toolbar_refresh, and
// the slide's grey treatment via that same action's broadcast_slide_refresh
// (both mirror the MCP write path's AiTools::BaseService broadcasts) --
// there is no extra wiring needed here for that.
//
// `data-controller` sits directly on the `<button>` itself (not a wrapping
// element -- see decks/_slide_toolbar.html.erb's comment on this button),
// matching the pins-toggle and Prior-cycle-toggle controllers beside it in
// the same toolbar -- so `this.element` here IS the button.
export default class extends Controller {
  static targets = ["eyeIcon", "eyeSlashIcon"]
  static values = { deckId: Number, slideUuid: String, hidden: Boolean }

  // UN-hiding only (not hiding) gets a confirm() guard -- card #3204 follow-
  // up, Jay's ask: it should be obvious a slide coming back off "hidden"
  // becomes client-visible again (Present/share link/PDF/PPTX), not just a
  // silent flip. Same plain `confirm()` pattern already used elsewhere in
  // this app for a single-click consequential action (e.g.
  // field_photos_controller.js, album_controller.js) rather than a custom
  // modal. Cancelling must be a true no-op -- no optimistic flip, no PATCH --
  // so the guard sits BEFORE _applyState/fetch, not wrapped around them.
  toggle(event) {
    event.preventDefault()

    const next = !this.hiddenValue
    if (!next) {
      const confirmed = confirm(
        "Unhide this slide? It will become visible again in Present, the public share link, and PDF/PPTX export."
      )
      if (!confirmed) return
    }

    const slideEl = this.element.closest(".airdrop-viewer__slide")
    this._applyState(next, slideEl)

    fetch(`/decks/${this.deckIdValue}/slide/${this.slideUuidValue}/toggle_hidden`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": this._csrfToken(),
      },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`toggle failed: ${response.status}`)
      })
      .catch(() => this._applyState(!next, slideEl))
  }

  _applyState(hidden, slideEl) {
    this.hiddenValue = hidden
    if (slideEl) slideEl.classList.toggle("is-hidden", hidden)

    this.eyeIconTarget.classList.toggle("hidden", hidden)
    this.eyeSlashIconTarget.classList.toggle("hidden", !hidden)

    this.element.setAttribute("aria-pressed", String(hidden))
    this.element.title = hidden ? "Show to client (currently hidden)" : "Hide from client"
    this.element.classList.toggle("bg-gray-100", hidden)
    this.element.classList.toggle("text-sapphire", hidden)
    this.element.classList.toggle("hover:bg-gray-200", hidden)
    this.element.classList.toggle("text-gray-600", !hidden)
    this.element.classList.toggle("hover:bg-gray-100", !hidden)
  }

  _csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }
}
